import { useState, useEffect } from 'react'
import {
  getSubmittalDetails,
  getSubmittalSchema,
  getActivityLogs,
} from '@/services/submittalService'
import { getFullJson, extractCombinedSchema } from '@/hooks/useEform'
import { capitalize } from '@/utils/helpers'
import useDataElements from '@/hooks/useDataElements'
import { labelFor } from '@/pages/Submittals/listing-config'

const initialState = {
  payload: null,
  schema: null,
  elementName: null,
  // Which data element this submittal was filed against. The listing knows it
  // from the URL; a detail page opened cold has only this, and the sidebar needs
  // it to keep the right element highlighted.
  dataElementId: null,
  // How the element is named to the reader, resolved the one way every other
  // submittals surface resolves it. `elementName` above is the raw attribute and
  // is not it: an element carrying a `listingConfig.tabName` is called that
  // everywhere else, so reading the attribute here would have the detail page
  // disagree with the sidebar sub-item and the listing heading it was opened
  // from — and an element with no `elementName` at all would leave the heading
  // blank rather than falling back to its id.
  listingLabel: null,
  status: null,
  modified: null,
  receiverOrgName: null,
  projectContractName: null,
  submissionMonth: null,
  // The instant a submittal was submitted, promoted onto its own column by the
  // service. The detail header shows it in place of the month for a data element
  // that submits on a date rather than for a month.
  submissionDate: null,
  // From the data element's own config: whether a second approval stage exists
  // and who performs it. Decides how a status reads — a consultant-approved
  // submittal on a two-stage element is awaiting the second approver, not done.
  secondaryApproval: null,
  // Also from the data element's config: whether its submittals are uploaded
  // spreadsheets or in-app forms. The two carry incompatible payloads — an array
  // of row-records against a single object — and neither renders the other's.
  submissionMode: null,
  // The payload as the API returned it. `payload` above has been through
  // getFullJson, which walks an object schema filling defaults; an array of
  // row-records is not that shape, so the upload table reads this instead.
  rawPayload: null,
  loading: false,
  error: null,
  notFound: false,
}

export const useSubmittalDetail = (submittalId, onError) => {
  const [state, setState] = useState(initialState)
  const [activityLogs, setActivityLogs] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const { dataElements } = useDataElements()

  const refetch = () => setRefreshKey(k => k + 1)

  useEffect(() => {
    if (!submittalId) return

    let aborted = false
    setState(s => ({ ...s, loading: true, error: null }))

    const load = async () => {
      const {
        payload: rawPayload,
        data_element_id: dataElementId,
        status,
        modified,
        receiver_org_name: receiverOrgName,
        submission_month: submissionMonthColumn,
        // Defaulted, not left undefined: the response omits the key entirely for a
        // submittal that has none (`omitempty`), and `initialState` holds null.
        submission_date: submissionDate = null,
        project_contract_name: projectContractColumn,
      } = await getSubmittalDetails(submittalId)

      // Read from the promoted root columns rather than out of the payload, the
      // same order pitstop's detail page resolves them in: the service
      // denormalises both for every submission mode, whereas an uploaded payload
      // is an array of row-records with no `project_details` group at its root —
      // read payload-first, the header shows nothing at all for an upload.
      //
      // Column-first is not a behaviour change for the eform elements. The
      // service fills the column from `project_details` first for an
      // object-rooted payload, so for those submittals the column and the
      // fallback hold the same string by construction, and the column has been on
      // the table since it was created — there are no older rows where it is
      // null. `submission_month` has read column-first for longer still.
      //
      // The one case where they differ: an eform save posts a partial payload
      // that drops the name, which the service deliberately does not clear the
      // column for. The last known name is shown rather than nothing, which is
      // what the listing already shows for that row.
      const projectContractName =
        projectContractColumn || rawPayload?.project_details?.project_contract_name || null
      const submissionMonth =
        submissionMonthColumn || rawPayload?.project_details?.submission_month || null

      const cached = dataElements?.find(
        el => el.dataElementId === dataElementId && el.elementSchema
      )
      const [schemaData, logs] = await Promise.all([
        cached ? Promise.resolve(cached) : getSubmittalSchema(dataElementId),
        getActivityLogs(submittalId).catch(err => {
          console.error('Failed to fetch activity logs:', err)
          return []
        }),
      ])
      const schemaItem = Array.isArray(schemaData) ? schemaData[0] : schemaData
      const { elementSchema: rawSchema, elementName } = schemaItem
      // Read off the same record as the schema: both the single-DE and all-DEs
      // endpoints apply the data element's config, so these are present whether
      // the record came from the cache or the network.
      const secondaryApproval = {
        required: schemaItem?.requiresSecondaryApproval === true,
        label: schemaItem?.secondaryApprovalLabel,
      }
      const submissionMode = schemaItem?.submissionMode ?? null
      const schema = extractCombinedSchema(rawSchema, rawPayload)
      // getFullJson walks an object schema filling in defaults, so it is only
      // meaningful for an eform payload. An array of row-records is passed
      // through untouched and read from rawPayload by the upload table.
      const payload = Array.isArray(rawPayload) ? rawPayload : getFullJson(schema, rawPayload)

      if (!aborted) {
        setState({
          payload,
          rawPayload,
          submissionMode,
          schema,
          elementName,
          dataElementId,
          listingLabel: labelFor(schemaItem),
          status,
          modified,
          receiverOrgName,
          projectContractName,
          submissionMonth,
          submissionDate,
          secondaryApproval,
          loading: false,
          error: null,
        })
        setActivityLogs(logs ?? [])
      }
    }

    load().catch(err => {
      if (!aborted) {
        const is404 = err.response?.status === 404
        const message = capitalize(err.response?.data?.detail) || err.message
        setState(s => ({ ...s, loading: false, error: message, notFound: is404 }))
      }
    })

    return () => {
      aborted = true
    }
  }, [submittalId, refreshKey])

  return { state, activityLogs, refetch }
}
